import React from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { Flag } from '@/components/Flag';
import type { CountryRate } from '@/types/pricing';

interface CountrySelectorProps {
    selectedCountry: CountryRate | null;
    countries?: CountryRate[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (country: CountryRate) => void;
    disabled?: boolean;
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({
    selectedCountry,
    countries,
    open,
    onOpenChange,
    onSelect,
    disabled,
}) => {
    const { t } = useTranslation('app');
    const { formatCurrency } = useCurrency();

    return (
        <div className="flex flex-col items-center shrink-0 gap-0.5">
            <Popover open={!disabled && open} onOpenChange={!disabled ? onOpenChange : undefined}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-1 px-1 h-auto py-0.5"
                        disabled={disabled || !countries}
                    >
                        {selectedCountry ? (
                            <Flag countryCode={selectedCountry.countryCode} size="md" />
                        ) : (
                            <Globe className="w-5 h-5 text-muted-foreground" />
                        )}
                        {!disabled && <ChevronsUpDown className="w-3 h-3 text-muted-foreground" />}
                    </Button>
                </PopoverTrigger>
                {!disabled && (
                    <PopoverContent className="w-80 p-0" align="start">
                        <Command>
                            <CommandInput placeholder={t('dialer.searchCountry')} />
                            <CommandList>
                                <CommandEmpty>{t('dialer.noCountriesFound')}</CommandEmpty>
                                <CommandGroup>
                                    {countries?.map((country) => (
                                        <CommandItem
                                            key={country.countryCode}
                                            value={`${t(`countries.${country.countryCode}`)} ${country.dialingCode}`}
                                            onSelect={() => onSelect(country)}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selectedCountry?.countryCode === country.countryCode ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            <Flag countryCode={country.countryCode} size="md" className="mr-3" />
                                            <div className="flex-1">
                                                <div className="font-medium">{t(`countries.${country.countryCode}`)}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    {t('dialer.countryDialingRate', {
                                                        dialingCode: country.dialingCode,
                                                        rate: formatCurrency(country.mobileRate, { decimalPlaces: 3 }),
                                                    })}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                )}
            </Popover>
        </div>
    );
};
